import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useMe } from "@/api/services/main/auth";
import { TGetMeResDto } from "@/api/services/main/auth/dtos/get-me.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";

type TSyncMeOrgNameArgs =
  | { type: "rso"; id?: number | null; name?: string | null }
  | { type: "company"; id?: number | null; name?: string | null };

/**
 * 詳細データ (RSO / 企業) で名前が更新されたら、ヘッダー (useMe) のキャッシュにも反映する。
 * get-me は連携同期後の新しい名前を返さない場合があるため、詳細の値を正としてキャッシュを書き換える。
 * ログイン中ユーザー自身の組織 (id 一致) のときのみ更新し、他組織の閲覧では書き換えない。
 */
export const useSyncMeOrgName = ({ type, id, name }: TSyncMeOrgNameArgs) => {
  const queryClient = useQueryClient();
  const { data: me } = useMe();

  // Current cached name for the targeted org; a background get-me refetch flips this back to the
  // stale server value, which re-triggers the effect below so the patch is re-applied.
  const cachedName = type === "rso" ? me?.data?.rso?.name : me?.data?.company?.companyName;

  useEffect(() => {
    if (id == null || !name) return;

    queryClient.setQueryData<TGetMeResDto>([QUERY_KEYS.USER.FETCH_ME], (prev) => {
      if (!prev?.data) return prev;

      if (type === "rso") {
        const rso = prev.data.rso;
        if (rso?.id !== id || rso.name === name) return prev;
        return { ...prev, data: { ...prev.data, rso: { ...rso, name } } };
      }

      const company = prev.data.company;
      if (company?.id !== id || company.companyName === name) return prev;
      return { ...prev, data: { ...prev.data, company: { ...company, companyName: name } } };
    });
  }, [queryClient, type, id, name, cachedName]);
};
